/**
 * A kΩ meter for one contact: where the reading sits against the four words.
 *
 * The scale is logarithmic (dry electrodes span two decades), from METER_MIN
 * to METER_MAX kΩ; the three word boundaries from quality.ts are marked on
 * it so the bar and the word can never disagree. Pure: no DOM. Ported from
 * `calibrations/static/capture/meter.mjs`.
 */

import { THRESHOLDS_KOHM, WORDS, tierOf, type Tier } from './quality'

export const METER_MIN = 10 // kΩ at the left edge
export const METER_MAX = 5000 // kΩ at the right edge

function pctOf(kohm: number): number {
  const v = Math.min(METER_MAX, Math.max(METER_MIN, kohm))
  const lo = Math.log10(METER_MIN)
  const hi = Math.log10(METER_MAX)
  return Math.round(((Math.log10(v) - lo) / (hi - lo)) * 10000) / 100
}

/** Positions (0–100) of the word boundaries, for the ticks. */
export const BOUNDARY_PCT = Object.freeze({
  good: pctOf(THRESHOLDS_KOHM.good),
  usable: pctOf(THRESHOLDS_KOHM.usable),
  weak: pctOf(THRESHOLDS_KOHM.weak),
})

export interface MeterReading {
  /** null for no reading */
  pct: number | null
  tier: Tier
  word: string
  /** "312 kΩ" · "1.5 MΩ" · "—" */
  value: string
}

/** meter(kohm) → { pct, tier, word, value } — pct is null for no reading. */
export function meter(kohm: unknown): MeterReading {
  const tier = tierOf(kohm)
  const finite = typeof kohm === 'number' && Number.isFinite(kohm)
  const v = finite ? (kohm as number) : NaN
  return {
    pct: finite ? pctOf(v) : null,
    tier,
    word: WORDS[tier],
    value: finite ? (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)} MΩ` : `${Math.round(v)} kΩ`) : '—',
  }
}
